import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useModal } from '../contexts/ModalContext'; // [NEW] Use Modal Hook
import AaronFace from './AaronFace';
import { appId } from '../firebase';
import { summarizeAttendance, summarizeFinance, summarizeSchedule, summarizeVisitLogs } from '../utils'; // [NEW] 요약 함수

export default function AaronWidget({ user, students, teachers, visitLogs, finances, schedules, attendanceLogs, chatHistory, setChatHistory, onNavigate, isOpen, onToggle, onClose, selectedDepartment, theme }) {
    const { openModal } = useModal(); // [NEW] Use Modal Hook
    // [NEW] Filter Data based on Selected Department
    // [FIX] 하위 호환성: department || grade 패턴 사용
    const filteredStudents = useMemo(() => {
        if (!selectedDepartment || selectedDepartment === 'All') return students;
        return students.filter(s => (s.department || s.grade) === selectedDepartment);
    }, [students, selectedDepartment]);

    const filteredTeachers = useMemo(() => {
        if (!selectedDepartment || selectedDepartment === 'All') return teachers;
        return teachers.filter(t => t.charge && t.charge.includes(selectedDepartment));
    }, [teachers, selectedDepartment]);

    const filteredFinances = useMemo(() => {
        if (!selectedDepartment || selectedDepartment === 'All') return finances;
        return finances.filter(f => !f.department || f.department === 'All' || f.department === selectedDepartment);
    }, [finances, selectedDepartment]);

    const filteredSchedules = useMemo(() => {
        if (!selectedDepartment || selectedDepartment === 'All') return schedules;
        return schedules.filter(s => !s.department || s.department === 'All' || s.department === selectedDepartment);
    }, [schedules, selectedDepartment]);

    const filteredVisitLogs = useMemo(() => {
        if (!selectedDepartment || selectedDepartment === 'All') return visitLogs;
        const studentIds = new Set(filteredStudents.map(s => s.id));
        return visitLogs.filter(log => studentIds.has(log.studentId));
    }, [visitLogs, filteredStudents, selectedDepartment]);
    // const [isOpen, setIsOpen] = useState(false);
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const scrollRef = useRef(null);

    // [NEW] 최근 사용 프롬프트 (User-Specific)
    const [recentPrompts, setRecentPrompts] = useState([]);

    // [NEW] 사용자별 최근 질문 로드
    useEffect(() => {
        if (!user?.uid) return;
        try {
            const saved = localStorage.getItem(`aaron_recent_prompts_${user.uid}`);
            setRecentPrompts(saved ? JSON.parse(saved) : []);
        } catch (e) {
            console.error("Failed to load recent prompts", e);
        }
    }, [user?.uid]);

    const recommendedPrompts = [
        "이번 주 주일 출석 현황 알려줘",
        "장기 결석자 명단 뽑아줘",
        "이번 주 설교 예화 추천해줘"
    ];

    const [suggestions, setSuggestions] = useState([]); // [NEW] 제안 상태 추가

    // [NEW] 아론의 먼저 제안하기 (Proactive) - 위젯 열릴 때 1회 실행
    useEffect(() => {
        const fetchSuggestions = async () => {
            if (!isOpen || suggestions.length > 0) return; // 이미 있거나 닫혀있으면 스킵
            try {
                // 요약 데이터 준비
                const summaryContext = {
                    attendance: summarizeAttendance(filteredStudents, attendanceLogs),
                    visit: summarizeVisitLogs(filteredStudents, filteredVisitLogs)
                };

                const res = await fetch('/api/chat', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        mode: 'analysis',
                        uid: user?.uid,
                        students: filteredStudents,
                        teachers: filteredTeachers,
                        visitLogs: filteredVisitLogs,
                        context: summaryContext // [NEW] 분석에도 요약 데이터 전달
                    })
                });
                const data = await res.json();
                if (data.suggestions) setSuggestions(data.suggestions);
            } catch (e) {
                console.error("제안 가져오기 실패", e);
            }
        };
        fetchSuggestions();
    }, [isOpen]);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [chatHistory, isOpen]);

    // [FIX] 외부에서 열기 요청 시(isOpen=true) 위젯(버튼) 자체도 다시 보이게 처리
    useEffect(() => {
        if (isOpen) setIsVisible(true);
    }, [isOpen]);

    const handlePromptClick = (text) => {
        // [MOD] 즉시 전송
        setInput(text);
        setTimeout(() => sendMessage(text), 0);
    };

    // [NEW] 제한 기능 상태
    const [cooldown, setCooldown] = useState(false);
    const [dailyCount, setDailyCount] = useState(0);
    const MAX_DAILY_LIMIT = 20;

    // [NEW] 하루 제한 횟수 로드
    // [NEW] 하루 제한 횟수 로드
    useEffect(() => {
        if (!user) return;
        const today = new Date().toDateString();
        const savedDate = localStorage.getItem(`aaron_limit_date_${user.uid}`);
        const savedCount = parseInt(localStorage.getItem(`aaron_daily_count_${user.uid}`) || '0', 10);

        if (savedDate !== today) {
            // 날짜가 바뀌었으면 초기화
            localStorage.setItem(`aaron_limit_date_${user.uid}`, today);
            localStorage.setItem(`aaron_daily_count_${user.uid}`, '0');
            setDailyCount(0);
        } else {
            setDailyCount(savedCount);
        }
    }, [user]);

    const sendMessage = async (textOverride = null) => {
        const messageToSend = typeof textOverride === 'string' ? textOverride : input;
        if (!messageToSend.trim() || cooldown) return;

        // 2. 하루 제한 체크
        if (dailyCount >= MAX_DAILY_LIMIT) {
            setChatHistory(prev => [...prev, { role: 'ai', text: `죄송합니다, 목사님! 😭\n하루 질문 한도(${MAX_DAILY_LIMIT}회)를 모두 사용하셨어요.\n내일 더 맑은 정신으로 도와드릴게요! 🙏` }]);
            return;
        }

        const userMsg = messageToSend;

        // [MOD] 부모 상태 업데이트
        setChatHistory(prev => [...prev, { role: 'user', text: userMsg }]);

        // [NEW] 최근 사용 프롬프트 저장 (중복 제거, 최대 3개, User-Specific)
        const newRecents = [userMsg, ...recentPrompts.filter(p => p !== userMsg)].slice(0, 3);
        setRecentPrompts(newRecents);
        if (user?.uid) {
            localStorage.setItem(`aaron_recent_prompts_${user.uid}`, JSON.stringify(newRecents));
        }

        setInput("");
        setIsLoading(true);
        setCooldown(true); // 1. 쿨다운 시작

        try {
            // 3. 생각하는 척 (최소 2초 딜레이 보장)
            const minDelayPromise = new Promise(resolve => setTimeout(resolve, 2000));

            // [NEW] 카운트 증가 저장
            // [NEW] 카운트 증가 저장
            const newCount = dailyCount + 1;
            setDailyCount(newCount);
            if (user) {
                localStorage.setItem(`aaron_daily_count_${user.uid}`, newCount.toString());
            }

            // [NEW] 데이터 요약 (토큰 최적화)
            const summaryContext = {
                attendance: summarizeAttendance(filteredStudents, attendanceLogs),
                finance: summarizeFinance(filteredFinances),
                schedule: summarizeSchedule(filteredSchedules),
                visit: summarizeVisitLogs(filteredStudents, filteredVisitLogs) // [NEW] 심방 데이터 추가
            };

            const apiCallPromise = fetch('/api/chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    mode: 'chat',
                    message: userMsg,
                    history: chatHistory.map(m => ({ role: m.role === 'user' ? 'user' : 'model', parts: [{ text: m.text }] })),
                    uid: user?.uid,
                    appId: appId,
                    context: { ...summaryContext },
                    students: filteredStudents.map(s => ({ name: s.name, grade: s.grade, faithState: s.faithState })),
                    teachers: filteredTeachers.map(t => ({ name: t.name, class: t.class }))
                })
            });

            // 최소 딜레이와 API 호출 동시에 기다림
            const [_, res] = await Promise.all([minDelayPromise, apiCallPromise]);

            const data = await res.json();
            // [MOD] 부모 상태 업데이트
            setChatHistory(prev => [...prev, { role: 'ai', text: data.reply }]);
        } catch (e) {
            setChatHistory(prev => [...prev, { role: 'ai', text: "오류가 발생했습니다." }]);
        } finally {
            setIsLoading(false);
            // 1. 쿨다운 해제 (3초 후 재질문 가능 - API 시간 포함)
            setTimeout(() => setCooldown(false), 1000);
        }
    };

    const handleHeaderClick = () => {
        if (onNavigate) {
            onNavigate();
            onClose();
        }
    };

    // [NEW] 위젯 숨김 상태
    const [isVisible, setIsVisible] = useState(true);

    // [NEW] 드래그 관련 상태 ref로 관리 (렌더링 최소화)
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const isDragging = useRef(false);
    const dragStartPos = useRef({ x: 0, y: 0 });
    const dragStartTime = useRef(0);

    const handleMouseDown = (e) => {
        // 우클릭 무시
        if (e.button !== 0) return;

        isDragging.current = false;
        dragStartPos.current = { x: e.clientX - position.x, y: e.clientY - position.y };
        dragStartTime.current = Date.now();

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = (e) => {
        e.preventDefault();
        const newX = e.clientX - dragStartPos.current.x;
        const newY = e.clientY - dragStartPos.current.y;

        // 드래그 거리 감지 (클릭과 구분)
        const moveDistance = Math.sqrt(Math.pow(newX - position.x, 2) + Math.pow(newY - position.y, 2));
        if (moveDistance > 5) {
            isDragging.current = true;
            setPosition({ x: newX, y: newY });
        }
    };

    const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);

        // 드래그가 아니었고 짧은 클릭이었다면 토글 실행
        if (!isDragging.current && (Date.now() - dragStartTime.current < 200)) {
            // [MOD] 토글 대신 부모 상태 변경 요청 (이미 onToggle이 prop으로 전달됨)
            onToggle();
        }
    };

    // [MOD] 부모 상태를 따라가도록 수정 (내부 visiblity 제거)
    // if (!isVisible) return null;

    return (
        <div
            className="fixed bottom-6 right-6 z-50 font-sans touch-none"
            style={{
                zIndex: 9999,
                transform: `translate(${position.x}px, ${position.y}px)`,
                cursor: isDragging.current ? 'grabbing' : 'pointer'
            }}
            onMouseDown={handleMouseDown}
        >

            {/* 채팅창 (위젯 위치 따라감) */}
            {isOpen && (
                <div
                    className="absolute bottom-20 right-0 w-[340px] h-[520px] bg-[#FAFAFA] rounded-[2rem] shadow-2xl border border-gray-200 flex flex-col overflow-hidden chat-enter cursor-default font-sans"
                    onMouseDown={(e) => e.stopPropagation()} // 채팅창 내부 클릭 시 드래그 방지
                >

                    {/* [MOD] 헤더 클릭 시 이동 - AIAssistantView 스타일 */}
                    <div
                        className="bg-white/80 backdrop-blur-md p-4 border-b border-gray-100 flex justify-between items-center cursor-pointer hover:bg-white transition-colors"
                        onClick={handleHeaderClick}
                        title="크게 보기 (AI 비서실로 이동)"
                    >
                        <div className="flex items-center gap-2">
                            <span className={`text-xl font-black font-do-hyeon ${theme?.text || 'text-[#354F38]'} tracking-tight`}>Aaron</span>
                            <span className="px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-[10px] font-bold">Gemini 3.0 Flash</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openModal({
                                        type: 'confirm',
                                        props: {
                                            title: '대화 초기화',
                                            message: '대화 내역을 모두 지우고\n새로운 대화를 시작하시겠습니까?',
                                            confirmButtonClass: 'bg-red-50 text-red-500 hover:bg-red-100',
                                            onConfirm: () => setChatHistory([])
                                        }
                                    });
                                }}
                                className="p-1.5 hover:bg-gray-100 rounded-full text-gray-400 hover:text-red-500 transition-colors"
                                title="대화 초기화"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path><path d="M21 3v5h-5"></path><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path><path d="M3 21v-5h5"></path></svg>
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); onClose(); }} className="p-1.5 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                            </button>
                        </div>
                    </div>

                    <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-5 custom-scrollbar">
                        {/* [NEW] 아론의 제안 버블 (상단 고정 느낌) */}
                        {suggestions.length > 0 && chatHistory.length === 0 && (
                            <div className="flex flex-col gap-2 mb-4 animate-fade-in-up">
                                <div className="flex items-center gap-2 text-xs font-bold text-gray-400 ml-1 mb-1">
                                    <span className="text-yellow-500">⚡</span> 아론의 오늘의 제안
                                </div>
                                {suggestions.map((s, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => handlePromptClick(s.context?.prompt || s.message)} // [MOD] 제안 클릭 시 실제 프롬프트 입력
                                        className="bg-white border border-emerald-100 shadow-sm p-4 rounded-2xl text-left hover:border-emerald-300 hover:shadow-md transition-all group"
                                    >
                                        <div className="text-xs font-bold text-emerald-700 mb-1">{s.title}</div>
                                        <div className="text-xs text-slate-600 leading-snug font-medium">{s.message}</div>
                                    </button>
                                ))}
                            </div>
                        )}

                        {chatHistory.map((m, i) => (
                            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}>
                                {m.role === 'ai' && (
                                    <div className="flex-shrink-0 mt-1 mr-2">
                                        <div className="w-8 h-8 rounded-xl bg-white shadow-sm border border-gray-100 p-0.5 flex items-center justify-center">
                                            <AaronFace size="100%" />
                                        </div>
                                    </div>
                                )}
                                <div className={`
                                    max-w-[85%] p-3.5 text-[14px] leading-relaxed shadow-sm border
                                    ${m.role === 'user'
                                        ? 'bg-[#354F38] text-white rounded-2xl rounded-tr-sm border-transparent'
                                        : 'bg-white border-gray-100 text-slate-700 rounded-2xl rounded-tl-sm'
                                    }
                                `}>
                                    {m.text}
                                </div>
                            </div>
                        ))}
                        {isLoading && (
                            <div className="flex justify-start animate-fade-in">
                                <div className="flex-shrink-0 mt-1 mr-2">
                                    <div className="w-8 h-8 rounded-xl bg-white shadow-sm border border-gray-100 p-0.5">
                                        <AaronFace size="100%" isTalking />
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 h-9 px-3 bg-white rounded-2xl rounded-tl-sm shadow-sm border border-gray-100">
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#354F38] animate-bounce"></span>
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#354F38] animate-bounce delay-100"></span>
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#354F38] animate-bounce delay-200"></span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* [NEW] 추천 프롬프트 영역 */}
                    <div className="px-4 py-3 bg-[#FAFAFA] border-t border-gray-100 flex gap-2 overflow-x-auto custom-scrollbar">
                        {(recentPrompts.length > 0 ? recentPrompts : recommendedPrompts).map((text, idx) => (
                            <button
                                key={idx}
                                onClick={() => handlePromptClick(text)}
                                className="whitespace-nowrap px-3 py-1.5 bg-white border border-gray-200 rounded-full text-[11px] font-medium text-slate-500 hover:bg-[#354F38] hover:text-white hover:border-[#354F38] transition-colors shadow-sm"
                            >
                                {text}
                            </button>
                        ))}
                    </div>

                    <div className="p-4 bg-white border-t border-gray-100">
                        <div className="flex items-center gap-2 bg-gray-50 rounded-[1.5rem] px-2 py-2 border border-gray-200 hover:border-emerald-200 focus-within:ring-1 focus-within:ring-emerald-200 focus-within:border-emerald-200 transition-all shadow-inner">
                            <input
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                                placeholder="무엇이든 물어보세요..."
                                className="flex-1 bg-transparent px-3 text-sm outline-none text-slate-700 placeholder:text-slate-400 font-medium"
                            />
                            <button
                                onClick={sendMessage}
                                disabled={!input.trim()}
                                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${input.trim() ? 'bg-[#354F38] text-white shadow-md hover:scale-105' : 'bg-gray-200 text-gray-400'}`}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 아론 버튼 (SVG) */}
            <div className="relative group">
                <button className="w-16 h-16 rounded-full shadow-xl hover:scale-105 active:scale-95 transition-transform duration-200 flex items-center justify-center relative overflow-hidden bg-white border border-slate-100">
                    {/* 드래그 핸들 힌트 (hover시 노출) */}
                    <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none z-10">
                        <span className="text-[10px] text-white font-bold bg-black/50 px-2 rounded-full backdrop-blur-sm">DRAG</span>
                    </div>
                    <AaronFace size={64} isTalking={isLoading} />
                </button>

                {/* [NEW] 위젯 닫기 버튼 (Hover시 우측 상단 노출) */}
                <button
                    onClick={(e) => { e.stopPropagation(); onClose(); }} // [MOD] 부모 onClose 호출
                    className="absolute -top-1 -right-1 w-6 h-6 bg-gray-500 hover:bg-red-500 text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-all shadow-md z-20"
                    title="위젯 끄기"
                    onMouseDown={(e) => e.stopPropagation()} // 닫기 버튼 클릭 시 드래그 방지
                >
                    ✕
                </button>
            </div>
        </div>
    );
}
